import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import posthog from "posthog-js";
import { Card, Button, Image, Segment, Grid, Header, Divider, Icon, Container, Modal } from "semantic-ui-react";
import { Badge } from "../../common";
import moment from "moment";
import * as leaveApprovalActions from "./leaveApprovalActions";
import { Common } from "../../../utils";
import { isSubscriptionSuspended, fetchSubscriptionData } from "../../../utils/subscriptionUtils";
import SubscriptionRestriction from "../../common/subscriptionRestriction/SubscriptionRestriction";
import "./MyLeaveRequests.css";

class MyLeaveRequests extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: "pending",
            searchText: "",
            isSubscriptionSuspended: false,
            showTextModal: false,
            textModalTitle: "",
            textModalContent: "",
        };
    }

    async componentDidMount() {
        // Check subscription status
        try {
            const subscriptionData = await fetchSubscriptionData();
            if (subscriptionData && isSubscriptionSuspended(subscriptionData)) {
                this.setState({ isSubscriptionSuspended: true });
                return;
            }
        } catch (error) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("myLeaveRequestsSubscription", {
                    type: "exception",
                    success: false,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    status: error?.response?.status,
                });
            }
        }

        // Load stats and pending requests by default
        this.props.leaveApprovalActions.getSelfRequestedLeavesStats();
        this.props.leaveApprovalActions.getSelfRequestedLeaves(0, 1, 10, false);
    }

    componentWillUnmount() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    }

    handleLoadMore = () => {
        const { leaveApprovalReducer } = this.props;
        const { selfLeavesPagination } = leaveApprovalReducer;
        const { activeTab } = this.state;

        if (selfLeavesPagination.hasMore && !leaveApprovalReducer.selfLeavesLoadingMore) {
            const nextPage = selfLeavesPagination.currentPage + 1;
            const status = this.getStatusValue(activeTab);
            this.props.leaveApprovalActions.getSelfRequestedLeaves(status, nextPage, selfLeavesPagination.pageSize, true);
        }
    };

    handleTabChange = (newTab) => {
        this.setState({ activeTab: newTab });
        const status = this.getStatusValue(newTab);
        this.props.leaveApprovalActions.getSelfRequestedLeaves(status, 1, 10, false);
    };

    getStatusValue = (tab) => {
        switch (tab) {
            case "pending": return 0;
            case "approved": return 1;
            case "rejected": return 2;
            case "canceled": return 3;
            case "all": return null;
            default: return null;
        }
    };

    handleShowFullText = (title, content) => {
        this.setState({
            showTextModal: true,
            textModalTitle: title,
            textModalContent: content || "",
        });
    };

    handleCloseTextModal = () => {
        this.setState({
            showTextModal: false,
            textModalTitle: "",
            textModalContent: "",
        });
    };

    getDefaultAvatar = (name) => {
        if (!name) return this.getGenericAvatar();

        const initials = name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 2);

        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
            '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
        ];
        const colorIndex = name.charCodeAt(0) % colors.length;
        const backgroundColor = colors[colorIndex];

        const svg = `
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="20" fill="${backgroundColor}"/>
                <text x="20" y="26" font-family="Arial, sans-serif" font-size="14" font-weight="bold" 
                      text-anchor="middle" fill="white">${initials}</text>
            </svg>
        `;

        return `data:image/svg+xml;base64,${btoa(svg)}`;
    };

    getGenericAvatar = () => {
        const svg = `
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="20" fill="#E0E0E0"/>
                <circle cx="20" cy="16" r="6" fill="#9E9E9E"/>
                <path d="M8 32c0-6.627 5.373-12 12-12s12 5.373 12 12" fill="#9E9E9E"/>
            </svg>
        `;
        return `data:image/svg+xml;base64,${btoa(svg)}`;
    };

    getHalfDayPeriod = (startingLeave, endingLeave) => {
        if (startingLeave === 0 || startingLeave === 'Morning' || startingLeave === 'AM') {
            return '(Morning)';
        } else if (startingLeave === 1 || startingLeave === 'Afternoon' || startingLeave === 'PM') {
            return '(Afternoon)';
        } else if (endingLeave === 0 || endingLeave === 'Morning' || endingLeave === 'AM') {
            return '(Morning)';
        } else if (endingLeave === 1 || endingLeave === 'Afternoon' || endingLeave === 'PM') {
            return '(Afternoon)';
        } else if (startingLeave === 2 || startingLeave === 'Evening') {
            return '(Evening)';
        } else if (endingLeave === 2 || endingLeave === 'Evening') {
            return '(Evening)';
        }
        return '(Half Day)';
    };

    getStatusBadge = (status) => {
        const statusConfig = {
            0: { color: "yellow", text: "Pending", icon: "clock" },
            1: { color: "green", text: "Approved", icon: "check circle" },
            2: { color: "red", text: "Rejected", icon: "times circle" },
            3: { color: "grey", text: "Canceled", icon: "ban" },
        };

        const config = statusConfig[status] || statusConfig[0];
        return (
            <Badge color={config.color} size="small" icon={config.icon}>
                {config.text}
            </Badge>
        );
    };

    getFilteredRequests = () => {
        const { selfLeaves } = this.props.leaveApprovalReducer;
        return selfLeaves || [];
    };

    renderRequestCard = (request) => {
        const startDate = moment(request.startDate).format("MMM DD, YYYY");
        const endDate = moment(request.endDate).format("MMM DD, YYYY");
        const isMultiDay = request.isMultiDayRequest || startDate !== endDate;

        // Get user info for avatar
        let userName = "You";
        try {
            const userInfo = JSON.parse(localStorage.userInfo);
            userName = userInfo.name || "You";
        } catch (e) {
            // Use request.name if available
            userName = request.name || "You";
        }

        return (
            <Card key={request.leaveId} className="my-leave-request-card">
                <Card.Content>
                    <div className="request-header">
                        <div className="employee-info">
                            <Image
                                src={request.profilePicture || this.getDefaultAvatar(userName)}
                                size="mini"
                                circular
                                className="employee-avatar"
                                onError={(e) => {
                                    const defaultAvatar = this.getDefaultAvatar(userName);
                                    if (e.target.src !== defaultAvatar) {
                                        e.target.src = defaultAvatar;
                                    }
                                }}
                            />
                            <div>
                                <Header as="h4" className="employee-name">
                                    {userName}
                                </Header>
                                <p className="employee-details">
                                    {request.designation && `${request.designation}`}
                                    {request.designation && request.departmentName && ' '}
                                    {request.departmentName && `${request.departmentName}`}
                                </p>
                                {request.officeName && (
                                    <p className="employee-office">
                                        <Icon name="building" size="small" />
                                        {request.officeName}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="request-status">
                            {this.getStatusBadge(request.approvalStatus)}
                        </div>
                    </div>

                    <Divider />

                    <div className="leave-details">
                        <div className="leave-type" style={{ 
                            color: request.leaveTypeColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Icon name="calendar" />
                                <strong>{request.leaveType}</strong>
                            </div>
                            {request.leaveDays > 0 && (
                                <span className="leave-days" style={{ 
                                    color: '#3b82f6',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                }}>
                                    ({request.leaveDays === 0.5 ?
                                        `0.5 day ${this.getHalfDayPeriod(request.startingLeave, request.endingLeave)}` :
                                        request.leaveDays === 1 ? '1 day' :
                                            `${request.leaveDays} days`})
                                </span>
                            )}
                        </div>
                        <div className="leave-dates">
                            <Icon name="clock" />
                            <span>{isMultiDay ? `${startDate} - ${endDate}` : startDate}</span>
                        </div>
                        <div className="leave-note" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            gap: '6px',
                            minHeight: '24px'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '6px', 
                                flex: 1,
                                overflow: 'hidden'
                            }}>
                                <Icon name="sticky note" />
                                <span 
                                    title={request.leaveNote && request.leaveNote.length > 50 ? request.leaveNote : undefined}
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1
                                    }}
                                >
                                    {request.leaveNote && request.leaveNote.trim() 
                                        ? (request.leaveNote.length > 50
                                            ? `${request.leaveNote.substring(0, 50)}...`
                                            : request.leaveNote)
                                        : '-'
                                    }
                                </span>
                            </div>
                            {request.leaveNote && request.leaveNote.length > 50 && (
                                <Button
                                    icon="expand"
                                    size="mini"
                                    compact
                                    basic
                                    onClick={() => this.handleShowFullText("Leave Note", request.leaveNote)}
                                    style={{
                                        padding: '4px 8px',
                                        fontSize: '11px',
                                        flexShrink: 0
                                    }}
                                >
                                    View Full
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="request-meta">
                        <small>
                            <Icon name="user" />
                            Approver: {request.approverName || 'Not assigned'}
                        </small>
                        <small>
                            <Icon name="calendar outline" />
                            Requested: {moment(request.requestDate).format("MMM DD, YYYY")}
                        </small>
                        {request.approvalDate && (
                            <small>
                                <Icon name="check circle" />
                                {request.approvalStatus === 1 ? 'Approved' : request.approvalStatus === 2 ? 'Rejected' : 'Updated'}: {moment(request.approvalDate).format("MMM DD, YYYY")}
                            </small>
                        )}
                    </div>

                    {request.approvalStatus !== 0 && (
                        <>
                            <Divider style={{ margin: '12px 0' }} />
                            <div className="approval-comment-section" style={{
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                borderLeft: `3px solid ${request.approvalStatus === 1 ? '#10b981' : request.approvalStatus === 2 ? '#ef4444' : '#6b7280'}`
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '8px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <Icon
                                            name="comment"
                                            size="small"
                                            color={request.approvalStatus === 1 ? 'green' : request.approvalStatus === 2 ? 'red' : 'grey'}
                                        />
                                        <strong style={{
                                            fontSize: '13px',
                                            color: '#374151',
                                            fontWeight: '600'
                                        }}>
                                            Approver Comment:
                                        </strong>
                                    </div>
                                    {request.approvalComment && request.approvalComment.trim() && request.approvalComment.length > 50 && (
                                        <Button
                                            icon="expand"
                                            size="mini"
                                            compact
                                            basic
                                            onClick={() => this.handleShowFullText("Approver Comment", request.approvalComment)}
                                            style={{
                                                padding: '4px 8px',
                                                fontSize: '11px',
                                                flexShrink: 0
                                            }}
                                        >
                                            View Full
                                        </Button>
                                    )}
                                </div>
                                <p style={{
                                    margin: 0,
                                    fontSize: '13px',
                                    color: '#4b5563',
                                    lineHeight: '1.5',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {request.approvalComment && request.approvalComment.trim() 
                                        ? (request.approvalComment.length > 50
                                            ? `${request.approvalComment.substring(0, 50)}...`
                                            : request.approvalComment)
                                        : '-'
                                    }
                                </p>
                            </div>
                        </>
                    )}
                </Card.Content>
            </Card>
        );
    };

    render() {
        const { leaveApprovalReducer } = this.props;
        const { selfLeavesLoading, selfLeavesLoadingMore, error, selfLeavesPagination, selfLeavesStats } = leaveApprovalReducer;
        const { activeTab, isSubscriptionSuspended, showTextModal, textModalTitle, textModalContent } = this.state;

        if (isSubscriptionSuspended) {
            return (
                <div style={{ padding: "20px" }}>
                    <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessLeaveRequests" />
                </div>
            );
        }

        const filteredRequests = this.getFilteredRequests();
        // Use stats from API if available, otherwise calculate from current filtered list
        const requestCounts = selfLeavesStats && Object.keys(selfLeavesStats).length > 0 ? {
            pending: selfLeavesStats.pendingApprovals || 0,
            approved: selfLeavesStats.approvedRequests || 0,
            rejected: selfLeavesStats.rejectedRequests || 0,
            canceled: selfLeavesStats.canceledRequests || (selfLeavesStats.totalRequests - (selfLeavesStats.pendingApprovals || 0) - (selfLeavesStats.approvedRequests || 0) - (selfLeavesStats.rejectedRequests || 0)),
            all: selfLeavesStats.totalRequests || 0
        } : {
            pending: filteredRequests.filter(r => r.approvalStatus === 0).length,
            approved: filteredRequests.filter(r => r.approvalStatus === 1).length,
            rejected: filteredRequests.filter(r => r.approvalStatus === 2).length,
            canceled: filteredRequests.filter(r => r.approvalStatus === 3).length,
            all: filteredRequests.length
        };

        return (
            <Container className="py-4 px-3 px-md-4" fluid>
                <div className="my-leave-requests-dashboard">
                    {/* Professional Header Section */}
                    <div className="my-leave-header-bar">
                        <div className="my-leave-header-content">
                            <div className="my-leave-header-left">
                                <div className="my-leave-header-icon-wrapper">
                                    <Icon name="calendar check" style={{ margin: 0, fontSize: '20px' }} />
                                </div>
                                <div className="my-leave-header-text">
                                    <h1 className="my-leave-page-title">
                                        {this.props.t("app.myLeaveRequests.title")}
                                    </h1>
                                    <p className="my-leave-page-description">
                                        {this.props.t("app.myLeaveRequests.description")}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => this.props.history.push("/dashboard")}
                                style={{
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    fontWeight: '500',
                                    borderWidth: '2px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <i className="bi bi-arrow-left me-2"></i>{this.props.t("app.myLeaveRequests.backToDashboard")}
                            </button>
                        </div>
                    </div>

                    {/* Statistics */}
                    <Grid columns={5} stackable className="statistics-grid" style={{ marginBottom: '30px' }}>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #fbbf24',
                                borderTop: '4px solid #f59e0b',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#f59e0b', marginBottom: '8px' }}>
                                    {requestCounts.pending}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.myLeaveRequests.pending")}
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #10b981',
                                borderTop: '4px solid #059669',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#059669', marginBottom: '8px' }}>
                                    {requestCounts.approved}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.myLeaveRequests.approved")}
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #ef4444',
                                borderTop: '4px solid #dc2626',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>
                                    {requestCounts.rejected}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.myLeaveRequests.rejected")}
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #6b7280',
                                borderTop: '4px solid #4b5563',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#4b5563', marginBottom: '8px' }}>
                                    {requestCounts.canceled}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    Canceled
                                </div>
                            </div>
                        </Grid.Column>
                        <Grid.Column>
                            <div style={{
                                background: '#ffffff',
                                padding: '25px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '2px solid #3b82f6',
                                borderTop: '4px solid #2563eb',
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#2563eb', marginBottom: '8px' }}>
                                    {requestCounts.all}
                                </div>
                                <div style={{ fontSize: '1em', color: '#6b7280', fontWeight: '500' }}>
                                    {this.props.t("app.myLeaveRequests.total")}
                                </div>
                            </div>
                        </Grid.Column>
                    </Grid>

                    {/* Status Tabs */}
                    <Segment className="filters-section" style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        marginBottom: '20px',
                        padding: '20px 24px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            flexWrap: 'wrap'
                        }}>
                            <Button.Group style={{
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                display: 'flex',
                                flexWrap: 'wrap'
                            }}>
                                <Button
                                    active={activeTab === "pending"}
                                    onClick={() => this.handleTabChange("pending")}
                                    style={{
                                        backgroundColor: activeTab === "pending" ? '#fef3c7' : '#ffffff',
                                        color: activeTab === "pending" ? '#f59e0b' : '#6b7280',
                                        border: activeTab === "pending" ? '2px solid #f59e0b' : '2px solid #e5e7eb',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        borderRadius: activeTab === "pending" ? '8px 0 0 8px' : '0',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Icon name="clock" style={{ marginRight: '6px' }} />
                                    {this.props.t("app.myLeaveRequests.pending")} ({requestCounts.pending})
                                </Button>
                                <Button
                                    active={activeTab === "approved"}
                                    onClick={() => this.handleTabChange("approved")}
                                    style={{
                                        backgroundColor: activeTab === "approved" ? '#d1fae5' : '#ffffff',
                                        color: activeTab === "approved" ? '#059669' : '#6b7280',
                                        border: activeTab === "approved" ? '2px solid #059669' : '2px solid #e5e7eb',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Icon name="check circle" style={{ marginRight: '6px' }} />
                                    {this.props.t("app.myLeaveRequests.approved")} ({requestCounts.approved})
                                </Button>
                                <Button
                                    active={activeTab === "rejected"}
                                    onClick={() => this.handleTabChange("rejected")}
                                    style={{
                                        backgroundColor: activeTab === "rejected" ? '#fee2e2' : '#ffffff',
                                        color: activeTab === "rejected" ? '#dc2626' : '#6b7280',
                                        border: activeTab === "rejected" ? '2px solid #dc2626' : '2px solid #e5e7eb',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Icon name="times circle" style={{ marginRight: '6px' }} />
                                    {this.props.t("app.myLeaveRequests.rejected")} ({requestCounts.rejected})
                                </Button>
                                <Button
                                    active={activeTab === "canceled"}
                                    onClick={() => this.handleTabChange("canceled")}
                                    style={{
                                        backgroundColor: activeTab === "canceled" ? '#f3f4f6' : '#ffffff',
                                        color: activeTab === "canceled" ? '#6b7280' : '#6b7280',
                                        border: activeTab === "canceled" ? '2px solid #6b7280' : '2px solid #e5e7eb',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Icon name="ban" style={{ marginRight: '6px' }} />
                                    {this.props.t("app.myLeaveRequests.canceled")} ({requestCounts.canceled})
                                </Button>
                                <Button
                                    active={activeTab === "all"}
                                    onClick={() => this.handleTabChange("all")}
                                    style={{
                                        backgroundColor: activeTab === "all" ? '#dbeafe' : '#ffffff',
                                        color: activeTab === "all" ? '#2563eb' : '#6b7280',
                                        border: activeTab === "all" ? '2px solid #2563eb' : '2px solid #e5e7eb',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        borderRadius: '0 8px 8px 0',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Icon name="list" style={{ marginRight: '6px' }} />
                                    All ({requestCounts.all})
                                </Button>
                            </Button.Group>
                        </div>
                    </Segment>

                    {/* Requests List */}
                    {selfLeavesLoading ? (
                        <Segment loading>
                            <div style={{ height: "200px" }}></div>
                        </Segment>
                    ) : error ? (
                        <Segment color="red">
                            <Header as="h4" color="red">
                                <Icon name="warning" />
                                Error
                            </Header>
                            <p>{error}</p>
                        </Segment>
                    ) : filteredRequests.length === 0 ? (
                        <Segment textAlign="center" className="empty-state">
                            <Icon name="inbox" size="huge" color="grey" />
                            <Header as="h3" color="grey">
                                {this.props.t("app.myLeaveRequests.noRequests")}
                            </Header>
                            <p>You don't have any leave requests matching the current filter.</p>
                        </Segment>
                    ) : (
                        <div>
                            <Card.Group className="requests-grid">
                                {filteredRequests.map(this.renderRequestCard)}
                            </Card.Group>

                            {/* Load More Button */}
                            {selfLeavesPagination && selfLeavesPagination.hasMore && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <Button
                                        primary
                                        loading={selfLeavesLoadingMore}
                                        onClick={this.handleLoadMore}
                                        disabled={selfLeavesLoadingMore}
                                    >
                                        {selfLeavesLoadingMore ? 'Loading...' : 'Load More'}
                                    </Button>
                                    <p style={{ marginTop: '10px', color: '#666' }}>
                                        Showing {filteredRequests.length} of {selfLeavesPagination.totalItems || filteredRequests.length} requests
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Text Viewer Modal */}
                    <Modal 
                        open={showTextModal} 
                        onClose={this.handleCloseTextModal} 
                        size="small"
                        className="lapproval-modal"
                    >
                        <Modal.Header className="lapproval-modal-header-gradient">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icon name="file text outline" />
                                <span>{textModalTitle}</span>
                            </div>
                        </Modal.Header>
                        <Modal.Content scrolling>
                            <div style={{
                                padding: '16px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                minHeight: '100px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                <p style={{
                                    margin: 0,
                                    fontSize: '14px',
                                    color: '#374151',
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {textModalContent}
                                </p>
                            </div>
                        </Modal.Content>
                        <Modal.Actions className="modal-actions-container">
                            <Button
                                onClick={this.handleCloseTextModal}
                                className="btn-cancel"
                            >
                                <Icon name="cancel" /> Close
                            </Button>
                        </Modal.Actions>
                    </Modal>
                </div>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    leaveApprovalReducer: state.leaveApprovalReducer,
});

const mapDispatchToProps = (dispatch) => ({
    leaveApprovalActions: bindActionCreators(leaveApprovalActions, dispatch),
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(MyLeaveRequests));
